import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { ShiftRateNegotiationStatus } from "@src/appV2/OpenShifts/RateNegotiation/types";
import { useGetQualificationDisplayLabels } from "@src/appV2/Qualifications/api/useGetQualificationDisplayLabels";
import { type ModalShiftData } from "@src/appV2/redesign/Shift/types";
import { useShiftModalsDataContext } from "@src/appV2/redesign/ShiftDiscovery/useShiftModalsDataContext";
import type { UseModalState } from "@src/appV2/redesign/system/ui-components";
import { useGetFacilityBreakConfiguration } from "@src/appV2/Shifts/Shift/useGetFacilityBreakConfiguration";
import { useHistory } from "react-router-dom";

import {
  SHIFT_DISCOVERY_WORKPLACE_OPEN_SHIFTS_MODAL_PATH,
  SHIFT_DISCOVERY_WORKPLACE_PROFILE_MODAL_PATH,
} from "../../ShiftDiscovery/paths";
import { useWorkplaceModalsDataContext } from "../../ShiftDiscovery/useWorkplaceModalsDataContext";
import { getShiftPayRates } from "../../utils/calculateShiftPay";
import { useWorkplaceOpenShiftsData } from "../../Workplace/OpenShifts/useWorkplaceOpenShiftsData";
import { RegularShiftBookedBottomSheet } from "./RegularShiftBookedBottomSheet";

interface RegularShiftBookedBottomSheetContainerProps {
  modalState: UseModalState;
  shift: ModalShiftData;
}

export function RegularShiftBookedBottomSheetContainer(
  props: RegularShiftBookedBottomSheetContainerProps
) {
  const { modalState, shift } = props;

  const {
    workplace,
    attributes: { timeSlot, start, end, qualification, isUrgent },
  } = shift;

  const history = useHistory();
  const { generateModalPath } = useWorkplaceModalsDataContext();
  const { availableShiftBoost, appliedShiftBoost } = useShiftModalsDataContext();

  const { mandatoryBreakDurationInMinutes } = useGetFacilityBreakConfiguration({
    workplaceId: workplace.id,
  });

  const rateNegotiation =
    shift.rateNegotiation?.status === ShiftRateNegotiationStatus.ACCEPTED
      ? shift.rateNegotiation
      : undefined;

  const { totalPay, totalPayFormatted, hourlyPayFormatted } = getShiftPayRates({
    shift,
    rateNegotiationId: rateNegotiation?.id,
  });

  const {
    data: { unfilteredWorkplaceShifts },
    isLoading: openShiftsIsLoading,
  } = useWorkplaceOpenShiftsData(workplace);

  const hasOpenShifts = unfilteredWorkplaceShifts.length > 0;
  const shouldShowOpenShifts = hasOpenShifts && !isUrgent;

  const { data: qualificationDisplayLabels, isLoading: isLoadingQualification } =
    useGetQualificationDisplayLabels([qualification]);
  // eslint-disable-next-line prefer-destructuring
  const qualificationText = qualificationDisplayLabels[0];

  return (
    <RegularShiftBookedBottomSheet
      modalState={modalState}
      shiftStartIso={start}
      shiftEndIso={end}
      shiftTimeSlot={timeSlot}
      workplaceName={workplace.attributes.name}
      workplaceTimezone={workplace.attributes.location.timezone}
      shiftHourlyPay={hourlyPayFormatted}
      shiftFormattedTotalPay={totalPayFormatted}
      shiftTotalPay={totalPay}
      shiftBoost={appliedShiftBoost?.attributes.amount ?? availableShiftBoost?.attributes.amount}
      requiresBreak={workplace.attributes.requiresLunchBreak}
      breakDurationInMinutes={mandatoryBreakDurationInMinutes}
      isLoadingQualification={isLoadingQualification}
      qualificationText={qualificationText}
      workplaceActionLabel={
        shouldShowOpenShifts
          ? `See ${unfilteredWorkplaceShifts.length} workplace shifts`
          : "See workplace profile"
      }
      isWorkplaceActionLoading={openShiftsIsLoading}
      onWorkplaceActionClick={() => {
        logEvent(
          shouldShowOpenShifts
            ? APP_V2_USER_EVENTS.WORKPLACE_OPEN_SHIFTS_OPENED
            : APP_V2_USER_EVENTS.WORKPLACE_PROFILE_OPENED,
          {
            workplaceId: workplace?.id,
            trigger: "Shift Booked Bottom Sheet",
          }
        );
        const modalPath = shouldShowOpenShifts
          ? generateModalPath(SHIFT_DISCOVERY_WORKPLACE_OPEN_SHIFTS_MODAL_PATH, {
              workplaceId: workplace?.id,
            })
          : generateModalPath(SHIFT_DISCOVERY_WORKPLACE_PROFILE_MODAL_PATH, {
              workplaceId: workplace?.id,
            });
        modalState.closeModal();
        history.push(modalPath);
      }}
    />
  );
}
